import {
  type MutateOptions,
  type MutationKey,
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { useCallback, useRef } from "react";

export type SingleFlightResult<TData> =
  | { alreadyInFlight: true }
  | { alreadyInFlight: false; data: TData };

/**
 * A wrapper around `useMutation` that prevents concurrent executions of the same mutation.
 * Uses a ref-based guard to synchronously block re-entrant calls before React re-renders,
 * which prevents issues like double-tap submitting a form twice.
 *
 * Returns `SingleFlightResult<TData>`:
 * - `{ alreadyInFlight: false, data }` on successful execution
 * - `{ alreadyInFlight: true }` if a call is already in flight
 */
export function useSingleFlightMutation<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>(
  mutationKey: MutationKey,
  mutationFunction: (variables: TVariables) => Promise<TData>,
  options?: Omit<
    UseMutationOptions<SingleFlightResult<TData>, TError, TVariables, TContext>,
    "mutationKey" | "mutationFn"
  >
): Omit<UseMutationResult<SingleFlightResult<TData>, TError, TVariables, TContext>, "mutate"> {
  const isInFlightRef = useRef(false);

  const mutation = useMutation<SingleFlightResult<TData>, TError, TVariables, TContext>(
    mutationKey,
    async (variables) => {
      const result = await mutationFunction(variables);
      return { alreadyInFlight: false, data: result };
    },
    options
  );

  const guardedMutateAsync = useCallback(
    async (
      variables: TVariables,
      mutateOptions?: MutateOptions<SingleFlightResult<TData>, TError, TVariables, TContext>
    ): Promise<SingleFlightResult<TData>> => {
      if (isInFlightRef.current) {
        return { alreadyInFlight: true };
      }

      isInFlightRef.current = true;
      try {
        return await mutation.mutateAsync(variables, mutateOptions);
      } finally {
        isInFlightRef.current = false;
      }
    },
    [mutation]
  );

  // `mutate` is intentionally excluded. It's fire-and-forget, so there's no way to
  // communicate the result back to the caller. Use `mutateAsync` instead.
  const { mutate: _, ...rest } = mutation;
  return { ...rest, mutateAsync: guardedMutateAsync };
}
